/**
 * 편성 — 스케줄 · 캠페인, 그리고 "무엇이 나오는가" 의 규칙.
 */
import type { ManualChapter } from '../types.js';

export const SCHEDULES: ManualChapter = {
  slug: 'schedules',
  title: '스케줄',
  summary: '요일과 시간대에 따라 화면에 재생할 편성을 자동으로 바꿉니다. 예) 평일 점심에는 메뉴 안내.',
  menuPath: '/schedules',
  editCapability: 'schedule:write',
  sections: [
    {
      id: 'what',
      title: '스케줄이란',
      blocks: [
        {
          kind: 'text',
          text: '하루를 시간대로 나눈 **편성표**입니다. “평소에는 브랜드 영상, 평일 11:30~13:00 에는 점심 메뉴” 처럼 정해 두면 화면이 시각에 맞춰 자동으로 전환합니다.',
        },
        {
          kind: 'table',
          header: ['구성', '뜻'],
          rows: [
            ['평소 편성', '아래 규칙에 걸리지 않는 **모든 시간**에 재생하는 플레이리스트 또는 콘텐츠 하나'],
            ['시간대 규칙', '“어느 요일, 몇 시부터 몇 시까지는 이것” 한 줄. 여러 개 둘 수 있습니다.'],
            ['시각 기준', '시각을 어느 나라 시간으로 셀지(기본: 서울)'],
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '스케줄은 만든 뒤 화면에 배정해야 동작합니다',
          text: '스케줄 메뉴에서는 편성표만 만듭니다. [[화면]] 메뉴에서 화면을 골라 이 스케줄을 배정하면, 카드의 “미배정” 이 “화면 N대” 로 바뀝니다.',
        },
      ],
    },
    {
      id: 'create',
      title: '예시 — 평일 점심시간에 메뉴 재생하기',
      blocks: [
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[스케줄]] 메뉴에서 [[새 스케줄]] 을 누릅니다.' },
            { title: '“이름” 에 **매장 운영시간** 처럼 알아보기 쉬운 이름을 넣습니다.' },
            { title: '“평소 편성” 에서 평소 재생할 플레이리스트나 콘텐츠를 고릅니다.', body: '목록이 “플레이리스트” 와 “콘텐츠” 로 나뉘어 있습니다. 평소엔 영상 한 편만 틀면 되는 곳은 플레이리스트를 따로 만들 필요 없이 그 콘텐츠를 바로 고르세요 — 틀 것이 하나뿐이면 화면을 바꾸지 않고 그대로 계속 보입니다(영상은 끝나면 처음부터 되풀이하고, HTML·웹 페이지는 다시 불러오지 않으며, 템플릿의 시계는 그 자리에서 흐릅니다). 비워 두면 규칙이 없는 시간에는 대기 화면이 나옵니다.' },
            { title: '[[규칙 추가]] 를 누릅니다.', body: '평일 09:00~18:00 인 규칙 한 줄이 생깁니다.' },
            { title: '요일에서 [[평일]] 을 누르고, 시각을 **11:30 ~ 13:00** 으로 바꿉니다.', body: '하루 종일이면 [[종일]] 을 켜세요.' },
            { title: '“에는” 옆 목록에서 점심 메뉴 플레이리스트를 고릅니다.' },
            { title: '오른쪽 “하루 미리보기” 에서 요일을 눌러 가며 막대를 확인합니다.', body: '규칙을 고치는 왼쪽 열과 결과를 그리는 오른쪽 열이 나란히 있고, 오른쪽은 스크롤을 내려도 따라옵니다. 월요일을 누르면 11:30~13:00 구간만 색이 다르고, 토·일은 평소 편성만 보여야 맞습니다.' },
            { title: '경고 상자가 없으면 [[저장]] 합니다.' },
            { title: '[[화면]] 메뉴에서 재생할 화면에 이 스케줄을 배정합니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '저장하기 전에 미리보기로 확인하세요',
          text: '“하루 미리보기” 막대는 실제 화면이 쓰는 계산과 똑같습니다. 막대에 마우스를 올리면 그 구간에 무엇이 재생되는지 보입니다. **규칙과 막대는 서로 연결되어 있습니다** — 왼쪽에서 규칙 칸을 수정하면 그 규칙이 만든 구간만 또렷해지고 나머지는 흐려집니다. 반대로 오른쪽에서 구간이나 구간 목록의 줄을 누르면 그 구간을 만든 규칙으로 이동합니다. 규칙이 열 줄을 넘어가면 이 방법이 가장 빠릅니다. 요일 칩 아래의 **점**은 그날 규칙이 하나라도 나온다는 뜻입니다(점이 없으면 하루 종일 평소 편성). 규칙에 마우스를 올리거나 규칙을 고르면 그 규칙이 나오는 요일에 테두리가 생기고, 적혀 있지만 다른 규칙에 가려지는 요일은 점선으로 보입니다. 규칙을 고르면 막대도 그 규칙이 나오는 요일로 넘어갑니다.',
        },
      ],
    },
    {
      id: 'overlap',
      title: '규칙이 겹칠 때',
      blocks: [
        {
          kind: 'text',
          text: '같은 시간에 규칙 두 개가 걸리면 **우선순위 숫자가 큰 규칙**이 재생됩니다. 겹치는 규칙에만 “우선순위” 칸이 나타나고, 어떤 규칙과 겹치는지 문장으로 알려 줍니다.',
        },
        {
          kind: 'legend',
          title: '규칙에 붙는 표시',
          items: [
            { label: '익일까지', tone: 'warn', meaning: '끝 시각이 시작보다 이릅니다. 오타가 아니라 “**다음 날** 그 시각까지” 라는 뜻입니다. 예) 22:00 ~ 02:00' },
            { label: '재생 안 됨', tone: 'danger', meaning: '다른 규칙에 완전히 가려져 한 번도 재생되지 않습니다. 우선순위를 올리거나 시간대를 좁히세요.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '특정 날짜는 스케줄로 정할 수 없습니다',
          text: '스케줄은 **요일**로만 반복됩니다. 설 연휴·행사일처럼 달력의 특정 날짜만 다르게 재생하려면 [[캠페인]] 을 쓰세요.',
        },
      ],
    },
    {
      id: 'manage',
      title: '고치기·삭제',
      blocks: [
        {
          kind: 'text',
          text: '목록의 스케줄 카드는 **접혀 있습니다** — 이름 왼쪽의 화살표(또는 이름 뒤의 숫자 줄)를 누르면 그 스케줄의 평소 편성과 규칙이 펼쳐지고, **이름을 누르면 편집 창**이 열립니다(고칠 권한이 없으면 이름을 눌러도 펼쳐집니다). 맨 윗줄이 규칙에 걸리지 않는 시간에 재생되는 평소 편성입니다. 접힌 채로도 시각 기준·사용 중인 화면 수·규칙 개수는 이름 옆에 적혀 있습니다. 카드 오른쪽의 [[편집]] 으로 고치고, 옆의 [[…]] ▸ [[삭제]] 로 지웁니다. 화면이나 그룹에 배정되어 사용 중인 스케줄은 바로 삭제되지 않고, 어느 화면·그룹에서 쓰는지 목록으로 알려 줍니다 — 이름을 누르면 그 화면으로 갑니다. 같은 창의 “처리 방법” 에서 **다른 스케줄로 교체** 하거나 **연결 해제**(배정을 비움)를 골라 한 번에 지울 수도 있습니다. 저장한 변경은 대개 몇 초 안에(늦어도 약 30초) 화면에 반영됩니다. [[…]] ▸ [[복제]] 는 그 스케줄의 규칙을 채운 “스케줄 복제” 창을 엽니다(저장해야 생깁니다). 스케줄이 많으면 목록 위 검색칸에 이름 일부를 넣어 찾습니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '잘못 고쳤으면 앞 판으로 되돌립니다',
          text: '편집 창의 [[변경 이력]] 을 누르면 저장할 때마다 남은 판이 누가·언제와 함께 보입니다. 판을 펼쳐 내용을 확인하고 [[되돌리기]] 를 누르면 그때의 모습으로 돌아갑니다 — 되돌린 것도 새 판으로 남으니 다시 되돌릴 수 있습니다.',
        },
      ],
    },
  ],
};

export const CAMPAIGNS: ManualChapter = {
  slug: 'campaigns',
  title: '캠페인',
  summary: '정해진 기간 동안 평소 편성 위에 광고·행사 콘텐츠를 덮어 재생하고, 기간이 끝나면 자동으로 돌아옵니다.',
  menuPath: '/campaigns',
  editCapability: 'schedule:write',
  sections: [
    {
      id: 'what',
      title: '캠페인이란',
      blocks: [
        {
          kind: 'text',
          text: '“6월 한 달, 로비 화면에 신제품 광고” 처럼 **기간·대상**을 정해 평소 편성 위에 얹는 편성입니다. 끝나는 날이 지나면 따로 지우지 않아도 평소 편성으로 돌아갑니다.',
        },
        {
          kind: 'table',
          header: ['재생 방식', '어떻게 나오나', '이럴 때'],
          rows: [
            ['전체 대체', '기간 동안 평소 편성 대신 **캠페인만** 재생합니다.', '연휴 인사, 매장 행사일'],
            ['삽입', '평소 콘텐츠 **N개마다 1개씩** 끼워 넣습니다. 화면마다 끼우는 자리가 달라 모든 화면이 동시에 같은 광고를 재생하지 않습니다.', '광고, 짧은 홍보'],
          ],
        },
      ],
    },
    {
      id: 'create',
      title: '캠페인 만들기',
      blocks: [
        {
          kind: 'callout',
          tone: 'note',
          title: '대상은 “전체 화면” · “태그” · “그룹” 으로 정합니다',
          text: '태그는 “로비”, “수도권” 처럼 화면에 붙이는 이름표입니다. 화면 상세의 “기본 정보” 에서 태그 칸에 적고 Enter 를 누르면 붙고, × 로 뺍니다. 같은 태그를 붙인 화면들이 한꺼번에 캠페인 대상이 됩니다. **그룹**은 [[화면 그룹]] 메뉴에서 만든 화면 묶음(예: 강남 사옥)입니다 — 사옥·지점 단위로 걸 때 씁니다. 담당 범위가 정해진 계정은 맡은 그룹만 고를 수 있습니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[캠페인]] 메뉴에서 [[새 캠페인]] 을 누릅니다.' },
            { title: '이름을 넣고, 재생할 플레이리스트를 고릅니다.' },
            { title: '재생 방식을 고릅니다.', body: '삽입을 고르면 “삽입 간격” 칸에 몇 개마다 끼울지 넣습니다(기본 3).' },
            { title: '대상을 고릅니다.', body: '“태그로 지정” 을 고르면 “로비, 수도권” 처럼 쉼표로 여러 태그를 넣을 수 있습니다. 태그 중 **하나라도** 가진 화면이 대상입니다. “그룹으로 지정” 을 고르면 그룹을 체크합니다.' },
            { title: '시작·종료 날짜를 고릅니다.', body: '비워 두면 그쪽은 기한이 없습니다.' },
            { title: '필요하면 [[시간대 추가]] 로 요일·시각을 제한합니다.', body: '추가하지 않으면 기간 안에서 하루 종일 재생합니다.' },
            { title: '[[만들기]] 를 누릅니다.', body: '목록에 “예정” 또는 “게재 중” 으로 나타납니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '캠페인끼리 겹치면',
          text: '같은 화면·같은 시간에 캠페인이 여러 개 걸리면 **우선순위 숫자가 큰 것 하나만** 재생됩니다. 숫자가 같으면 먼저 만든 캠페인이 우선합니다. 새로 만든 캠페인이 나오지 않으면 우선순위를 올려 보세요(기본 100) — 편집 창 아래의 [[고급 설정]] 을 펼치면 있습니다. 100 이 아닌 캠페인은 펼친 채로 열립니다.',
        },
      ],
    },
    {
      id: 'status',
      title: '목록의 상태 읽기',
      blocks: [
        {
          kind: 'legend',
          title: '상태 표시',
          items: [
            { label: '게재 중', tone: 'ok', meaning: '지금 기간 안에 있습니다. 게재 시간대를 정했다면 그 시간에만 실제로 나옵니다.' },
            { label: '예정', tone: 'info', meaning: '시작 날짜가 아직 오지 않았습니다.' },
            { label: '종료', tone: 'neutral', meaning: '기간이 끝났습니다. 화면은 평소 편성으로 돌아갔습니다.' },
            { label: '중지됨', tone: 'neutral', meaning: '[[중지]] 로 잠시 꺼 두었습니다. [[재개]] 로 다시 켭니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '“게재 중” 옆에 “가려짐 · 다른 캠페인” 이 붙었다면 재생되지 않습니다',
          text: '같은 대상에 우선순위가 더 높은 캠페인이 걸려 있다는 뜻입니다 — 켜져 있고 기간 안인데도 **어느 화면에도 재생되지 않습니다.** 우선하는 캠페인의 이름이 함께 적히니, 마우스를 올려 어느 우선순위에 밀렸는지 확인하고 그 캠페인을 수정하거나 이쪽 우선순위를 올리세요. 대상이 태그로 엇갈려 화면마다 결과가 다를 때는 표시하지 않습니다 — 그때는 화면 상세의 “편성 결정 근거” 가 그 화면의 결과를 알려 줍니다.',
        },
        {
          kind: 'text',
          text: '상태는 목록을 연 순간 기준입니다. 오래 열어 두었다면 메뉴를 다시 눌러 새로 확인하세요.',
        },
        {
          kind: 'text',
          text: '목록은 우선순위가 높은 순(같으면 먼저 만든 순)으로 나옵니다 — 재생할 캠페인을 고르는 순서와 같습니다. **이름**·**기간**·**우선순위** 열 이름을 누르면 그 열로 정렬합니다(기간은 시작일 기준). **상태** 열의 필터 아이콘으로 “게재 중” 이나 “예정” 만 골라 볼 수 있습니다. 줄 끝의 [[…]] ▸ [[복제]] 는 그 캠페인의 값을 채운 “캠페인 복제” 창을 엽니다 — 기간·대상만 바꿔 [[저장]] 하면 새 캠페인이 생깁니다(저장하기 전에는 아무것도 생기지 않습니다). 왼쪽 체크칸으로 여러 개를 골라 한 번에 [[중지]]·[[재개]]·[[삭제]] 할 수 있습니다.',
        },
      ],
    },
    {
      id: 'emergency',
      title: '긴급 오버라이드',
      blocks: [
        {
          kind: 'text',
          text: '재난·장애 안내처럼 **모든 화면에 즉시** 재생해야 할 때 쓰는 가장 강한 편성입니다. 관리자가 [[캠페인]] 메뉴 위쪽의 **긴급** 패널에서 발령하고 해제합니다. 작동 중에는 캠페인 목록 위에 빨간 띠가 나타나고, 상단 바의 종 모양 알림에도 “긴급 오버라이드 작동 중” 이 뜹니다. 직접 만든 편성이 갑자기 나오지 않는다면 이 띠부터 확인하세요.',
        },
        {
          kind: 'steps',
          title: '긴급 송출하기',
          steps: [
            { title: '[[캠페인]] 메뉴의 긴급 패널에서 [[긴급 송출]] 을 누릅니다.' },
            { title: '내보낼 것을 고릅니다.', body: '미리 만들어 둔 안내 플레이리스트를 고르거나, **화면 비우기**(검은 화면)를 고릅니다.' },
            { title: '대상(전체 화면 · 태그 · 그룹)과, 필요하면 끝낼 시각을 정합니다.', body: '담당 범위가 정해진 계정은 맡은 그룹에만 냅니다. 그 계정의 단추는 [[맡은 화면 비우기]] 입니다.' },
            { title: '확인란에 표시하고 [[발령]] 을 누릅니다.', body: '약 30초 안에 대상 화면이 바뀝니다. 평소 편성과 달리 다 받으면 **카운트다운 없이 곧바로** 틀고, 나가던 콘텐츠도 끝까지 기다리지 않습니다(처음 받는 영상은 내려받는 시간이 더해집니다). 꺼져 있던 화면은 켜지는 대로 받습니다. 운영 시간 밖이어도 나갑니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '잘못 나간 것을 당장 걷어야 할 때 — [[전 화면 비우기]]',
          text: '무엇이 잘못 나갔는지 확인할 시간이 없으면 긴급 패널 오른쪽의 [[…]] ▸ [[전 화면 비우기]] 를 누르세요. 모든 화면이 즉시 검게 섭니다. 원인을 찾아 그 콘텐츠를 보관한 뒤 빨간 띠의 [[즉시 해제]] 를 누르면 평소 편성으로 돌아갑니다. 발령·해제는 변경 기록에 남습니다(대시보드의 “최근 활동”).',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '반영은 몇 초 안에 — 꺼져 있던 화면은 켜지는 대로',
          text: '편성을 바꾸면 서버가 기다리던 화면을 곧바로 깨워 몇 초 안에 바뀝니다(늦어도 약 30초). 그때 꺼져 있던 화면은 다시 켜져 서버에 닿는 즉시 받습니다. 캠페인도 편집 창의 [[변경 이력]] 으로 앞 판에 되돌릴 수 있습니다.',
        },
      ],
    },
  ],
};

export const PRIORITY: ManualChapter = {
  slug: 'what-plays',
  title: '지금 무엇이 나오나 — 편성 규칙',
  summary: '긴급·바로 보내기·캠페인·스케줄이 한 화면에 겹칠 때 무엇이 먼저 적용되는지 한 장으로 정리합니다.',
  sections: [
    {
      id: 'order',
      title: '위에 있는 편성이 먼저 적용됩니다',
      blocks: [
        {
          kind: 'text',
          text: '화면은 약 30초마다 서버에 지금 재생할 편성을 확인하고, 아래 순서에서 **가장 위에 걸리는 것 하나**를 재생합니다. 위의 편성이 끝나면 자동으로 아래 편성이 다시 나옵니다.',
        },
        { kind: 'figure', figure: 'priority', caption: '편성의 우선순위 — 위에 있을수록 먼저 적용됩니다' },
        {
          kind: 'table',
          header: ['순서', '무엇', '어디서 정하나'],
          rows: [
            ['1', '**긴급 오버라이드** — 모든 것을 멈추고 이것만(또는 화면 비우기)', '캠페인 ▸ 긴급 — 관리자가 발령·해제, 캠페인 목록 위 빨간 띠로 알립니다'],
            ['2', '**바로 보내기** — 이 화면 한 대에만 임시로', '화면 상세 “편성 배정” 카드 · 화면 목록 일괄 막대'],
            ['—', '**운영 시간 밖** — 아래 편성은 모두 쉬고 화면이 검게 섭니다(위의 둘은 나갑니다)', '환경 설정 · 화면 상세의 [[화면 설정]]'],
            ['3', '**캠페인** — 기간·대상을 정해 덮어쓰기(전체 대체) 또는 끼워 넣기(삽입)', '캠페인'],
            ['4', '**화면에 배정한 스케줄·플레이리스트** — 스케줄이면 시각에 맞는 규칙, 없으면 “평소 편성”', '화면 상세'],
            ['5', '**그룹에 배정한 편성** — 화면에 따로 배정한 것이 없을 때 물려받습니다', '화면'],
            ['6', '대기 화면 — 아무것도 걸리지 않을 때', '—'],
          ],
        },
      ],
    },
    {
      id: 'examples',
      title: '자주 헷갈리는 상황',
      blocks: [
        {
          kind: 'faq',
          items: [
            {
              q: '스케줄을 고쳤는데 화면이 그대로입니다.',
              a: '그 화면에 캠페인이나 바로 보내기가 걸려 있으면 스케줄보다 그쪽이 먼저 재생됩니다. 화면 상세에서 지금 무엇 때문에 이 콘텐츠가 나오는지 확인하세요. 아무것도 걸려 있지 않다면 약 30초 기다려 보세요.',
            },
            {
              q: '캠페인이 “게재 중” 인데 나오지 않습니다.',
              a: '① 긴급 오버라이드가 작동 중인지(캠페인 목록 위 빨간 띠), ② 그 화면에 바로 보내기가 걸려 있는지, ③ 우선순위가 더 높은 다른 캠페인이 있는지, ④ 게재 시간대를 정해 두었다면 지금이 그 시간인지, ⑤ 화면에 캠페인의 태그가 붙어 있는지 차례로 확인하세요.',
            },
            {
              q: '플레이리스트에 넣은 콘텐츠 하나만 나오지 않습니다.',
              a: '① 그 콘텐츠의 **게시 기간**이 지났거나 아직 시작 전인지(콘텐츠 목록의 “게시 종료”·“D-3 종료” 같은 표시), ② 올린 영상이 아직 **변환 중**인지(“변환 대기”·“변환 중 N%”), ③ 보관했는지 확인하세요. 셋 다 어느 편성에 있어도 빠집니다.',
            },
            {
              q: '밤에 화면이 검게 나옵니다.',
              a: '환경 설정에 **운영 시간**이 정해져 있으면 그 시간 밖에는 화면이 쉽니다. 정상입니다. 그 화면만 늘 켜 두려면 화면 상세의 [[화면 설정]] 에서 “늘 켜기” 를 고르세요.',
            },
            {
              q: '바로 보내기를 해제하면 원래 편성은 어떻게 되나요?',
              a: '바로 보내기는 편성을 건드리지 않고 잠시 위에 얹는 것이라, 해제하면 원래 재생하던 편성으로 곧바로 돌아갑니다.',
            },
            {
              q: '해외 매장 화면의 시간이 맞지 않습니다.',
              a: '시각은 **화면에 정한 시간대 → 스케줄의 “시각 기준” → 서비스 기본값** 순으로 정해집니다. 화면 상세에서 그 화면의 시간대를 확인하세요.',
            },
          ],
        },
      ],
    },
  ],
};
